/**
 * Puente al SDK de autenticación externo (Auth0/Clerk/Cognito/Supabase/...).
 *
 * El SDK concreto se enchufa desde el Proxy holder (auth-client.ts) cuando
 * AuthContext registra la instancia de TGTAuthClient. Por defecto opera en
 * modo null y los services caerán al mock fallback (controlado por USE_MOCKS / VITE_USE_MOCKS en runtime-config).
 */
import { authClient } from "@/lib/auth-client";

export async function getAccessToken(): Promise<string | null> {
  try {
    return authClient.getToken() ?? null;
  } catch {
    return null;
  }
}

export function getActiveTenantId(): string | null {
  try {
    return authClient.getTenantId();
  } catch {
    return null;
  }
}

/**
 * Fuerza la renovación del access token contra el refresh token almacenado.
 * El SDK deduplica llamadas concurrentes (comparte una única promesa interna),
 * por lo que es seguro invocarlo desde varias peticiones fallidas en paralelo.
 */
export async function refreshAccessToken(): Promise<boolean> {
  try {
    return await authClient.refreshAccessToken();
  } catch {
    return false;
  }
}

const RETURN_PATH_KEY = "licita_return_path";

/**
 * Última red de seguridad cuando el access token expiró y no hay refresh
 * token con qué renovarlo en silencio (sesión sin refresh token emitido por
 * el core de identidad). Guarda la ruta actual y dispara el login SSO: si la
 * sesión del core sigue viva, el round-trip es casi instantáneo; si no,
 * muestra el login real, que es lo correcto.
 */
export function redirectToLoginPreservingPath(): void {
  try {
    sessionStorage.setItem(RETURN_PATH_KEY, window.location.pathname + window.location.search);
  } catch {
    // sessionStorage no disponible (modo privado, etc.): igual intentamos redirigir.
  }
  try {
    authClient.redirectToLogin(window.location.href);
  } catch {
    // Sin cliente activo no hay a dónde redirigir; nada más que hacer aquí.
  }
}

/** Recupera (y limpia) la ruta guardada antes del round-trip de login SSO. */
export function consumeReturnPath(): string | null {
  try {
    const path = sessionStorage.getItem(RETURN_PATH_KEY);
    if (path) sessionStorage.removeItem(RETURN_PATH_KEY);
    return path;
  } catch {
    return null;
  }
}
